import React from "react";
import { Form, RadioButton, RadioGroup } from "@natera/form";
import { Address } from "@app/provider/profile";
import { FormField } from "@app/components";
import { useErrorController } from "@natera/platform/lib/hooks";

import { getAddressAsString } from "@app/utils";

import "./selectAddressForm.scss";
import { defineMessages, useIntl } from "react-intl";

type Props = {
  addresses: Address[];
  onSubmit: (selectedAddress: Address) => void;
  actions: React.ReactNode;
};

const messages = defineMessages({
  selectAddressFormLegend: {
    id: "selectAddressFormLegend",
    defaultMessage: "Addresses",
  },
});

const SelectAddressForm: React.FC<Props> = ({
  addresses,
  onSubmit,
  actions,
}) => {
  const [addressUid, setAddressUid] = React.useState<string>(
    addresses[0]?.uid || ""
  );

  const { clearErrors, getValidationError } = useErrorController();
  const intl = useIntl();

  const handleSelectAddress = (addressUid: string) => setAddressUid(addressUid);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    clearErrors();

    const selectedAddress = addresses.find(
      (address) => address.uid === addressUid
    );

    if (!selectedAddress) {
      return;
    }

    onSubmit(selectedAddress);
  };

  return (
    <Form
      noValidate
      className="patient-addresses"
      buttons={actions}
      onSubmit={handleSubmit}
    >
      <fieldset>
        <legend className="visually-hidden">
          {intl.formatMessage(messages.selectAddressFormLegend)}
        </legend>
        <RadioGroup
          name="patientAddress"
          value={addressUid}
          onChange={handleSelectAddress}
        >
          {addresses.map((address) => (
            <FormField
              key={address.uid}
              htmlFor={address.uid}
              error={getValidationError(address.uid)}
              withPadding
            >
              <RadioButton
                data-testid={address.uid}
                className="patient-addresses__item"
                label={
                  <div className="address-item-desc">
                    {getAddressAsString(address)}
                  </div>
                }
                value={address.uid}
              />
            </FormField>
          ))}
        </RadioGroup>
      </fieldset>
    </Form>
  );
};

export default SelectAddressForm;
